import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { BasePopupActionPayload, BasePopupProps } from 'contexts/PopupContext';
import { MirrorService } from 'services/MirrorService';

import { useMedia } from 'hooks/useMedia';
import { usePackPrice } from 'hooks/usePackagePrice';
import { usePromoteVipPopupPayment } from 'hooks/usePromoteVipPopupPayment';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { CardMask } from 'components/shared/CardMask';
import { Popup } from 'components/UI/Popup';

import iconImg from './images/diamond-icon.jpg';
import { PromoteVipPopupBenefitsList } from './submodules/PromoteVipPopupBenefitsList';
import { PromoteVipPopupInfo } from './submodules/PromoteVipPopupInfo';
import { PromoteVipPopupLegal } from './submodules/PromoteVipPopupLegal';
import { PromoteVipPopupPrice } from './submodules/PromoteVipPopupPrice';

import css from './promoteVipPopup.module.sass';

type ActionsPayload = BasePopupActionPayload<'close'>;

type Props = BasePopupProps<null, (action: ActionsPayload) => void>;

export const PromoteVipPopup: React.FC<Props> = ({ onAction }) => {
  const { t } = useTranslation('app');

  const { isMobile } = useMedia();

  const {
    vipPackage,
    cardMask,
    isSubmitting,
    isVipDiscountAvailable,
    handlePay,
    handleClose,
  } = usePromoteVipPopupPayment({
    onClose: () => onAction({ type: 'close' }),
  });

  const packagePrice = usePackPrice({ package: vipPackage, isVip: true });

  const fakeInitialCost =
    MirrorService.isMePlusYouOrLocalsjoy ||
    MirrorService.isLocalsDateOrLustStation
      ? '1.4'
      : (Number(packagePrice?.afterDiscountByPeriod) * 1.455).toFixed(2);

  const initialPackageCost = packagePrice?.isHasDiscount
    ? String(packagePrice.initialByPeriod)
    : fakeInitialCost;

  const discountedPackageCost = packagePrice?.afterDiscountByPeriod;

  const initialPackagePrice = `${packagePrice?.applyCurrency(
    initialPackageCost
  )}/${packagePrice?.durationLabel}`;

  const discountedPackagePrice = `${packagePrice?.applyCurrency(
    discountedPackageCost
  )}/${packagePrice?.durationLabel}`;

  return (
    <Popup isFullScreen={isMobile} onClose={handleClose}>
      <div className={css.root}>
        <div className={css.imageHolder}>
          <img src={iconImg} className={css.image} alt="thumbnail" />
        </div>

        <h2 className={css.title}>
          <Trans
            i18nKey="promoteVipPopup.title"
            ns="app"
            components={{
              span: <span className={css.titleHighlight} />,
            }}
          />
        </h2>

        <section className={css.benefits}>
          <PromoteVipPopupBenefitsList />
        </section>

        <section className={css.price}>
          <PromoteVipPopupPrice
            initialPrice={initialPackagePrice}
            discountedPrice={discountedPackagePrice}
            isSpecialOffer={!isVipDiscountAvailable}
          />
        </section>

        <section className={css.info}>
          <PromoteVipPopupInfo />
        </section>

        <BaseButton
          type={ButtonTypes.Accent}
          className={css.submitBtn}
          loading={isSubmitting}
          onClick={handlePay}
        >
          {t('promoteVipPopup.submitBtn')}
        </BaseButton>

        {(cardMask || packagePrice) && (
          <footer className={css.footer}>
            {cardMask && (
              <section>
                <CardMask mask={cardMask} />
              </section>
            )}
            {packagePrice && (
              <section className={css.info}>
                <PromoteVipPopupLegal
                  currency={packagePrice.currency}
                  price={packagePrice.recurringInitial}
                  recurrentDuration={vipPackage.duration}
                />
              </section>
            )}
          </footer>
        )}

        {!!MirrorService.supportPhone && (
          <p className={css.supportPhone}>{MirrorService.supportPhone}</p>
        )}
      </div>
    </Popup>
  );
};
